// [FEAT-CUR 2026-05-23] Per-school currency hook + context.
//
// Single source of truth for money formatting across the app. Reads the
// current school's currency from:
//   1. <CurrencyProvider value={...}> (preferred — driven by /auth/me)
//   2. localStorage 'school_currency' (survives full page reload before
//      /auth/me has resolved)
//   3. 'XOF' default (FCFA — the platform's primary market)
//
// Usage:
//   const { code, symbol, decimals, formatAmount } = useSchoolCurrency();
//   formatAmount(150000)          → "150,000 FCFA"  (XOF/XAF)
//   formatAmount(1500, { withSymbol: false }) → "1,500.00"
//
// The decimal + symbol catalog mirrors backend/currencies.py exactly so the
// client and the server always agree on rendering.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { formatNumber } from '../lib/money';

// [ISSUE-2 fix 2026-07] Event name SchoolSettings dispatches after it saves a new
// currency, so the whole app re-renders in the chosen currency WITHOUT a re-login.
export const CURRENCY_CHANGED_EVENT = 'school-currency-changed';

// [BUGFIX-RTL 2026-05-23] Money + numeric strings must render LTR even
// inside RTL text — otherwise an Arabic context can put "FCFA" on the
// wrong side of "15,000". unicode-bidi: isolate keeps the run intact in mixed
// RTL/LTR neighbourhoods (CSS available globally via .amount class
// below, this is for inline use).
const LTR_STYLE = { direction: 'ltr', unicodeBidi: 'isolate' };
const AmountSpan = ({ children, className }) => (
  <span dir="ltr" style={LTR_STYLE} className={(className ? className + ' ' : '') + 'amount'}>
    {children}
  </span>
);

// Mirrors backend/currencies.py — keep in sync when adding currencies.
const CATALOG = {
  XOF: { symbol: 'FCFA', decimals: 0, name_en: 'West African CFA',  name_fr: 'Franc CFA BCEAO',     name_ar: 'فرنك غرب أفريقي' },
  XAF: { symbol: 'FCFA', decimals: 0, name_en: 'Central African CFA', name_fr: 'Franc CFA BEAC',   name_ar: 'فرنك وسط أفريقي' },
  NGN: { symbol: '₦',    decimals: 2, name_en: 'Naira',             name_fr: 'Naira',             name_ar: 'نيرة' },
  GHS: { symbol: '₵',    decimals: 2, name_en: 'Cedi',              name_fr: 'Cedi',              name_ar: 'سيدي' },
  KES: { symbol: 'KSh',  decimals: 2, name_en: 'Kenyan Shilling',   name_fr: 'Shilling kenyan',   name_ar: 'شلن كيني' },
  ETB: { symbol: 'Br',   decimals: 2, name_en: 'Ethiopian Birr',    name_fr: 'Birr éthiopien',    name_ar: 'بر إثيوبي' },
  TZS: { symbol: 'TSh',  decimals: 2, name_en: 'Tanzanian Shilling',name_fr: 'Shilling tanzanien',name_ar: 'شلن تنزاني' },
  UGX: { symbol: 'USh',  decimals: 0, name_en: 'Ugandan Shilling',  name_fr: 'Shilling ougandais',name_ar: 'شلن أوغندي' },
  RWF: { symbol: 'RF',   decimals: 0, name_en: 'Rwandan Franc',     name_fr: 'Franc rwandais',    name_ar: 'فرنك رواندي' },
  MAD: { symbol: 'DH',   decimals: 2, name_en: 'Moroccan Dirham',   name_fr: 'Dirham marocain',   name_ar: 'درهم مغربي' },
  DZD: { symbol: 'DA',   decimals: 2, name_en: 'Algerian Dinar',    name_fr: 'Dinar algérien',    name_ar: 'دينار جزائري' },
  TND: { symbol: 'DT',   decimals: 3, name_en: 'Tunisian Dinar',    name_fr: 'Dinar tunisien',    name_ar: 'دينار تونسي' },
  EGP: { symbol: 'E£',   decimals: 2, name_en: 'Egyptian Pound',    name_fr: 'Livre égyptienne',  name_ar: 'جنيه مصري' },
  GMD: { symbol: 'D',    decimals: 2, name_en: 'Gambian Dalasi',    name_fr: 'Dalasi gambien',    name_ar: 'دالاسي غامبي' },
  USD: { symbol: '$',    decimals: 2, name_en: 'US Dollar',         name_fr: 'Dollar US',         name_ar: 'دولار' },
  EUR: { symbol: '€',    decimals: 2, name_en: 'Euro',              name_fr: 'Euro',              name_ar: 'يورو' },
};

const DEFAULT_CODE = 'XOF';

const CurrencyContext = createContext(null);

const _readStored = () => {
  try { return localStorage.getItem('school_currency') || ''; }
  catch { return ''; }
};

// `code` must be uppercase 3 letters; falls back to default if unknown.
const _normalize = (code) => {
  if (!code) return DEFAULT_CODE;
  const up = String(code).toUpperCase();
  return CATALOG[up] ? up : DEFAULT_CODE;
};

// Format amount with a comma between thousands (matches backend
// currencies.format_amount): "150,000", so an extra zero shows (operator's
// request, 2026-10-28; it was a space). The digits come from lib/money.js.
// XOF/XAF/UGX/RWF render with 0 decimals; everything else 2 (TND uses 3).
//
// [BUGFIX-RTL 2026-05-23] By default the result is wrapped in an LTR
// <span> so it renders correctly inside Arabic-direction containers.
// Pass `asString: true` (or `withSymbol: false`) to get the raw plain
// string back for use in placeholders / titles / toast messages.
const buildFormatter = (code) => {
  const meta = CATALOG[code] || CATALOG[DEFAULT_CODE];
  const decimals = meta.decimals;
  return (amount, opts = {}) => {
    const num = formatNumber(amount, decimals);
    const text = (opts.withSymbol === false) ? num : `${num} ${meta.symbol}`;
    // Plain-string requests (titles, alt text, tooltips) skip the
    // wrapper so React doesn't barf on an object child.
    if (opts.asString || opts.withSymbol === false) return text;
    return <AmountSpan>{text}</AmountSpan>;
  };
};

export const CurrencyProvider = ({ currencyCode, children }) => {
  // [ISSUE-2 fix 2026-07] Hold the EFFECTIVE code in state so a runtime currency
  // change takes effect immediately. Previously the value was memoized on the
  // `currencyCode` prop (driven by /auth/me) and preferred it over localStorage,
  // so after an admin saved a new currency the whole app kept showing the OLD one
  // until a full re-login refreshed the user object.
  const [code, setCode] = useState(() => _normalize(currencyCode || _readStored()));

  // Adopt the server-provided code on login / auth refresh.
  useEffect(() => {
    if (currencyCode) setCode(_normalize(currencyCode));
  }, [currencyCode]);

  // Persist the server code so pre-login / offline boots render correctly.
  useEffect(() => {
    if (!currencyCode) return;
    try { localStorage.setItem('school_currency', currencyCode); }
    catch { /* private mode */ }
  }, [currencyCode]);

  // React to a runtime change (SchoolSettings save dispatches
  // CURRENCY_CHANGED_EVENT after writing localStorage) and to cross-tab
  // 'storage' events — no re-login required.
  useEffect(() => {
    const sync = () => setCode(_normalize(_readStored()));
    window.addEventListener(CURRENCY_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CURRENCY_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const value = useMemo(() => {
    const meta = CATALOG[code] || CATALOG[DEFAULT_CODE];
    return {
      code,
      symbol: meta.symbol,
      decimals: meta.decimals,
      name: meta,
      formatAmount: buildFormatter(code),
    };
  }, [code]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useSchoolCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (ctx) return ctx;
  // Standalone fallback for components rendered outside the provider tree
  // (tests, storybook, early boot). Reads from localStorage.
  const code = _normalize(_readStored());
  const meta = CATALOG[code];
  return {
    code,
    symbol: meta.symbol,
    decimals: meta.decimals,
    name: meta,
    formatAmount: buildFormatter(code),
  };
};

export const CURRENCY_CATALOG = CATALOG;

// [BUGFIX-RTL 2026-05-23] Generic LTR-isolated wrapper for any numeric
// display (percentages, counts, scores, dates) that should never flip
// in RTL text. Use as <Num>{value}</Num> or <Num>{x}%</Num>.
export const Num = ({ children, className }) => (
  <span dir="ltr" style={LTR_STYLE} className={(className ? className + ' ' : '') + 'amount'}>
    {children}
  </span>
);

export default useSchoolCurrency;
